import _extends from "@babel/runtime/helpers/extends";
import React, { useEffect, useState } from "react";
import { Badge, Box, Button, InputSelect, Picture } from "~/components-shared";
import api from "~/lib/api";
import { MembersSortByEnum } from "~/types/enums";
import lists from "~/lib/lists";
import styled, { useTheme } from "styled-components";
const StyledBox = styled(Box)`
  overflow: auto;
`;
const uniqueOptions = (options, selection, filtered) => {
  let combinedOptions = options;
  if (!filtered) {
    combinedOptions = [...selection, ...options];
  }
  return Array.from(new Set(combinedOptions.map(a => a.id))).map((id, index) => combinedOptions.find(a => a.id === id) || combinedOptions[index]);
};
const mapMemberToOption = (_ref, theme) => {
  let {
    id,
    firstName,
    lastName,
    role,
    email,
    type,
    profileImage
  } = _ref;
  return {
    id,
    showImage: true,
    image: profileImage,
    pre: role?.name,
    value: `${firstName} ${lastName}`,
    accessory: !!type && /*#__PURE__*/React.createElement(Picture, {
      border: "squared",
      src: lists.members.types.logos(theme)[type],
      width: "1.5rem",
      height: "1.5rem",
      backgroundSize: "contain",
      display: "inline-block",
      s: {
        mr: 1,
        mb: -1.5
      }
    }),
    post: email
  };
};
const InputMemberSelect = _ref2 => {
  let {
    withSearch = true,
    defaultMembers,
    defaultMember,
    filterMembers,
    onChange,
    multiple = false,
    ...rest
  } = _ref2;
  const theme = useTheme();
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [pagination, setPagination] = useState();
  const [selectedOptions, setSelectedOptions] = useState([]);
  const [teams, setTeams] = useState([]);
  const filteredTeams = teams.filter(team => team.name.toLowerCase().includes(searchQuery.toLowerCase()));
  const loadMembers = function () {
    let more = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : false;
    setLoading(true);
    api.fetchMembers({
      offset: more ? pagination?.after || undefined : undefined,
      size: 6,
      sort: MembersSortByEnum.BY_ROLE_DESC,
      filter: {
        query: searchQuery,
        memberIds: filterMembers
      }
    }).then(response => {
      setOptions(prev => [...(more ? prev : []), ...response.items.map(member => mapMemberToOption(member, theme))]);
      setTeams(response.teams);
      setPagination(response.pagination);
    }).finally(() => {
      setLoading(false);
    });
  };
  useEffect(() => {
    if (defaultMembers) {
      setSelectedOptions(defaultMembers.map(member => mapMemberToOption(member, theme)));
    }
  }, [JSON.stringify(defaultMembers)]);
  useEffect(() => {
    if (defaultMember) {
      setSelectedOptions([mapMemberToOption(defaultMember, theme)]);
    }
  }, [JSON.stringify(defaultMember)]);
  useEffect(() => {
    loadMembers();
  }, [searchQuery, JSON.stringify(filterMembers)]);
  const handleSearch = query => {
    setSearchQuery(query);
  };
  const handleChange = value => {
    if (multiple && Array.isArray(value)) {
      const items = uniqueOptions(options, selectedOptions, false).filter(option => value.includes(option.id));
      setSelectedOptions(items);
      if (onChange) {
        onChange(value);
      }
    } else if (selectedOptions.find(option => option.id === value)) {
      setSelectedOptions([]);
      if (onChange) {
        onChange("");
      }
    } else {
      const selectedOption = options.find(option => option.id === value);
      if (selectedOption) {
        setSelectedOptions([selectedOption]);
      }
      if (onChange) {
        onChange(value);
      }
    }
  };
  return /*#__PURE__*/React.createElement(InputSelect, _extends({
    options: uniqueOptions(options, selectedOptions, !!searchQuery),
    searchValue: searchQuery,
    onLoadMore: () => loadMembers(true),
    hasNextPage: pagination?.hasAfter,
    onSearch: handleSearch,
    loading: loading,
    onChange: handleChange,
    elemBefore: !!filteredTeams.length && multiple && /*#__PURE__*/React.createElement(StyledBox, {
      wrap: "nowrap",
      gap: 2,
      s: {
        px: 3,
        pt: 1,
        pb: 4
      }
    }, filteredTeams.map(team => /*#__PURE__*/React.createElement(Button, {
      s: {
        pr: 1,
        pl: 2,
        py: 1
      },
      style: {
        whiteSpace: "nowrap"
      },
      fontSize: 3,
      color: "translucent",
      onClick: () => {
        setSelectedOptions(team.members.map(member => mapMemberToOption(member, theme)));
        if (onChange) {
          onChange(team.members.map(member => member.id));
        }
      }
    }, team.name, /*#__PURE__*/React.createElement(Badge, {
      s: {
        py: 0.5,
        px: 2,
        ml: 1
      },
      color: "translucent"
    }, team.members.length)))),
    dropdownProps: {
      onClose: () => setSearchQuery(""),
      maxWidth: "350px",
      fullWidth: true
    },
    multiple: multiple,
    withSearch: true
  }, rest));
};
export default InputMemberSelect;